import React, { useState, useEffect, useCallback, useRef } from "react";
import cloneDeep from "lodash/cloneDeep";
import debounce from "lodash/debounce";
import map from "lodash/map";
import styled from "styled-components";
import Input from "components/bootstrap/Input";
import Select from "components/common/Select";
import Icon from "components/common/Icon";
import { Button, Table } from "components/bootstrap";
import { getValueFromInput } from "util/FormsUtils";
import ToolsStore from "stores/tools/ToolsStore";
import { isValidURL } from "util/URLUtils";
import generateId from "logic/generateId";
import useProductName from "brand-customization/useProductName";
const StyledTable = styled(Table)`
  margin-top: 10px;
`;
const validateUrlEntry = async (idx, entry, callback) => {
  const validationResult = {
    title: { valid: false },
    value: { valid: false }
  };
  validationResult.title = entry.title.trim().length <= 0 ? { valid: false } : { valid: true };
  let valueValidation = { valid: false };
  if (entry.type === "literal") {
    valueValidation = isValidURL(entry.value) ? { valid: true } : { valid: false };
  } else if (entry.type === "regex" && entry.value.trim().length > 0) {
    valueValidation = (await ToolsStore.testRegexValidity(entry.value)).is_valid ? { valid: true } : { valid: false };
  }
  validationResult.value = valueValidation;
  if (typeof callback === "function") {
    callback(idx, entry, validationResult);
  }
  return validationResult;
};
const debouncedValidateUrlEntry = debounce(validateUrlEntry, 200);
const UrlAllowListForm = ({ urls = [], onUpdate = () => {
}, disabled = false, newEntryId = void 0 }) => {
  const productName = useProductName();
  const literal = "literal";
  const regex = "regex";
  const options = [
    { value: literal, label: "Exact match" },
    { value: regex, label: "Regex" }
  ];
  let inputs = {};
  const [config, setConfig] = useState({ entries: urls, disabled });
  const [validationState, setValidationState] = useState({ errors: [] });
  const isInitialRender = useRef(false);
  const _onAdd = (event) => {
    event.preventDefault();
    setConfig({ ...config, entries: [...config.entries, { id: generateId(), title: "", value: "", type: literal }] });
  };
  const _onRemove = (event, idx) => {
    event.preventDefault();
    let stateUpdate = cloneDeep(config);
    const validationUpdate = cloneDeep(validationState);
    validationUpdate.errors[idx] = null;
    setValidationState(validationUpdate);
    stateUpdate.entries.splice(idx, 1);
    setConfig(stateUpdate);
  };
  const hasValidationErrors = useCallback(() => {
    let isValid = true;
    if (validationState.errors.length > 0 && validationState.errors.find(
      (el) => (el && el.title && el.title.valid) === false || el && el.value && el.value.valid === false
    )) {
      isValid = false;
    }
    return isValid;
  }, [validationState]);
  const _updateState = (idx, nextEntry) => {
    const stateUpdate = cloneDeep(config);
    stateUpdate.entries[idx] = nextEntry;
    setConfig(stateUpdate);
  };
  const _updateValidationError = (idx, nextEntry, entryValidation) => {
    setValidationState((prevValidationState) => {
      const nextValidationState = cloneDeep(prevValidationState);
      nextValidationState.errors[idx] = entryValidation;
      return nextValidationState;
    });
    _updateState(idx, nextEntry);
  };
  const _validate = async (name, idx, value) => {
    const nextEntry = { ...config.entries[idx], [name]: value };
    await debouncedValidateUrlEntry(idx, nextEntry, _updateValidationError);
  };
  const _onInputChange = (event, idx) => {
    _validate(event.target.name, idx, getValueFromInput(event.target));
  };
  const _onUpdateType = (idx, type) => {
    _validate("type", idx, type);
  };
  const _getErrorMessage = (type) => type === regex ? "Not a valid Java regular expression" : "Not a valid URL";
  const _getSummary = () => config.entries.map((url, idx) => /* @__PURE__ */ React.createElement("tr", { key: url.id }, /* @__PURE__ */ React.createElement("td", { style: { verticalAlign: "middle", textAlign: "center" } }, idx + 1), /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement(
    Input,
    {
      type: "text",
      id: `title-input${idx}`,
      ref: (elem) => {
        inputs[`title${idx}`] = elem;
      },
      help: validationState.errors[idx] && validationState.errors[idx].title && !validationState.errors[idx].title.valid ? "Required field" : null,
      name: "title",
      bsStyle: validationState.errors[idx] && validationState.errors[idx].title && !validationState.errors[idx].title.valid ? "error" : null,
      onChange: (event) => _onInputChange(event, idx),
      defaultValue: url.title,
      required: true
    }
  )), /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement(
    Input,
    {
      type: "text",
      id: `value-input${idx}`,
      ref: (elem) => {
        inputs[`value${idx}`] = elem;
      },
      help: validationState.errors[idx] && validationState.errors[idx].value && !validationState.errors[idx].value.valid ? _getErrorMessage(url.type) : null,
      name: "value",
      bsStyle: validationState.errors[idx] && validationState.errors[idx].value && !validationState.errors[idx].value.valid ? "error" : null,
      onChange: (event) => _onInputChange(event, idx),
      defaultValue: url.value,
      required: true
    }
  )), /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement(Input, { id: `url-input-type-${idx}`, required: true, autoFocus: true }, /* @__PURE__ */ React.createElement(
    Select,
    {
      clearable: false,
      options,
      placeholder: "Select url type",
      onChange: (option) => _onUpdateType(idx, option),
      value: url.type
    }
  ))), /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement(Button, { onClick: (event) => _onRemove(event, idx) }, /* @__PURE__ */ React.createElement(Icon, { name: "delete" }), /* @__PURE__ */ React.createElement("span", { className: "sr-only" }, "Delete entry")))));
  useEffect(() => {
    const isNewEntryValid = async () => {
      const newEntryIdx = config.entries.findIndex((entry) => entry.id === newEntryId);
      if (newEntryIdx < 0) {
        return false;
      }
      const newEntry = config.entries[newEntryIdx];
      const entryValidation = await validateUrlEntry(newEntryIdx, newEntry, _updateValidationError);
      return map(entryValidation, "valid").some((valid) => !!valid);
    };
    const propagateUpdate = async (firstRender) => {
      const valid = firstRender && newEntryId ? await isNewEntryValid() : hasValidationErrors();
      onUpdate(config, valid);
    };
    propagateUpdate(!isInitialRender.current);
    if (!isInitialRender.current) {
      isInitialRender.current = true;
    }
  }, [config]);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    Input,
    {
      type: "checkbox",
      id: "allowlist-disabled",
      label: "Disable Allowlist",
      checked: config.disabled,
      onChange: () => setConfig({ ...config, disabled: !config.disabled }),
      help: `Disable the allowlist functionality. Warning: Disabling this option will allow users to enter any URL in ${productName} entities, which may pose a security risk.`
    }
  ), /* @__PURE__ */ React.createElement(Button, { bsSize: "sm", onClick: (event) => _onAdd(event) }, "Add Url"), /* @__PURE__ */ React.createElement(StyledTable, { striped: true, bordered: true }, /* @__PURE__ */ React.createElement("thead", null, /* @__PURE__ */ React.createElement("tr", null, /* @__PURE__ */ React.createElement("th", null, "#"), /* @__PURE__ */ React.createElement("th", null, "Title"), /* @__PURE__ */ React.createElement("th", null, "URL"), /* @__PURE__ */ React.createElement("th", null, "Type"), /* @__PURE__ */ React.createElement("th", null, "Actions"))), /* @__PURE__ */ React.createElement("tbody", null, _getSummary())), /* @__PURE__ */ React.createElement(Button, { bsSize: "sm", onClick: (event) => _onAdd(event) }, "Add Url"));
};
export default UrlAllowListForm;
